/**
 * Pure, React-free state derivation for `/admin/automation/slots` (A7Y-3600) —
 * the `workloads-verdict.ts` precedent. Components render what this file
 * decides; they do not decide it themselves.
 */
import type { SlotState, UkkoSlotStatus, UkkoSlotWork } from '@a7y/types';
import { KIND_LABELS } from './shared';
import { STATE_META, UNKNOWN_META } from './lease-state-meta';

export interface SlotStateMeta {
  label: string;
  chip: string;
  card: string;
}

/**
 * `SlotState` → the same chip/card palette `LeaseStatusSection.tsx` uses for
 * `AutomationLeaseState`, reused wholesale rather than re-derived (D13/F2).
 * `unknown` maps to `UNKNOWN_META`, whose dashed border and neutral chip mean
 * it can never be styled as a healthy state — the same guarantee
 * `LeaseStatusSection` gives `null`.
 */
export const SLOT_STATE_META: Record<SlotState, SlotStateMeta> = {
  idle: { label: 'Idle', chip: STATE_META.free.chip, card: STATE_META.free.card },
  starting: { label: 'Starting', chip: STATE_META.reserved.chip, card: STATE_META.reserved.card },
  running: { label: 'Running', chip: STATE_META.active.chip, card: STATE_META.active.card },
  stuck: { label: 'Stuck', chip: STATE_META.quarantined.chip, card: STATE_META.quarantined.card },
  overtime: {
    label: 'Overtime',
    chip: STATE_META.handoff_pending.chip,
    card: STATE_META.handoff_pending.card,
  },
  unknown: { label: UNKNOWN_META.label, chip: UNKNOWN_META.chip, card: UNKNOWN_META.card },
};

/**
 * D5: `sessionKind` refined by `component`. `component` only distinguishes
 * within `sessionKind === 'cdid'` — every other ledger-writing kind
 * (`ahc`, `apd`, `cdid-worker-reconcile`, `addis-ingress-scheduler`,
 * `cdid-lander`, `cdid-hot-lane`, `aquix`, `state-invariants`, `manual`) is
 * self-distinguishing by kind alone, so it falls back to `KIND_LABELS` (the
 * canonical CDID/AHC/APD labels already used on the session-detail page)
 * and then to the bare kind string when there is no friendlier label.
 *
 * A job carrying no `work` (an idle slot) or no `sessionKind` (a non-session
 * job — the common case on a verify slot, C7) falls back to `workflowName`,
 * which is always present.
 */
export function slotTypeLabel(work: UkkoSlotWork | null): string {
  if (!work) return '—';
  const { sessionKind, component } = work;

  if (sessionKind === 'cdid') {
    if (component === 'worker') return 'CDID worker';
    if (component === 'ingress-scheduler') return 'ADDIS ingress';
    if (component === 'implementation-session') return 'CDID';
    // 'legacy-unclassified', or a null component on a 'cdid' ledger row —
    // fall through to the bare-kind branch below.
  }

  if (sessionKind) {
    return KIND_LABELS[sessionKind] ?? sessionKind;
  }

  return work.workflowName;
}

export interface GroupedSlots {
  session: UkkoSlotStatus[];
  verify: UkkoSlotStatus[];
  deploy: UkkoSlotStatus[];
}

/** Group slots by their configured class, each sorted by index ascending. */
export function groupSlots(slots: UkkoSlotStatus[]): GroupedSlots {
  const groups: GroupedSlots = { session: [], verify: [], deploy: [] };
  for (const slot of slots) {
    groups[slot.slotClass].push(slot);
  }
  groups.session.sort((a, b) => a.index - b.index);
  groups.verify.sort((a, b) => a.index - b.index);
  groups.deploy.sort((a, b) => a.index - b.index);
  return groups;
}
